<template>
  <div class="card border-0 shadow-none rounded-3 mb-4">
    <div class="card-body p-4 p-xl-40">
      <div class="toolbar-card mb-4">
        <div class="toolbar-left">
          <h4 class="mb-1">{{ $t("warehouse_page.list_title") }}</h4>
          <p class="text-muted mb-0">
            {{ $t("warehouse_page.list_description") }}
          </p>
        </div>
        <div class="toolbar-right">
          <div class="search-shell">
            <input
              :value="searchQuery"
              type="text"
              class="form-control toolbar-search"
              :placeholder="$t('warehouse_page.search_placeholder')"
              @input="$emit('update:searchQuery', ($event.target as HTMLInputElement).value)"
            />
            <i class="ri-search-line search-icon"></i>
          </div>
          <button class="btn style-one btn-sm d-flex align-items-center gap-2" @click="$emit('create')">
            <i class="ri-add-circle-line"></i>
            <span>{{ $t("warehouse_page.create_button") }}</span>
          </button>
        </div>
      </div>

      <a-table
        :columns="columns"
        :data-source="warehouses"
        :row-key="'id'"
        :pagination="paginationConfig"
        :locale="{ emptyText: $t('warehouse_page.no_warehouses_found') }"
        class="warehouse-table"
      >
        <template #bodyCell="{ column, record, text }">
          <template v-if="column.key === 'status'">
            <span class="status-badge" :class="Number(record.status ?? 1) === 1 ? 'status-active' : 'status-inactive'">
              {{ Number(record.status ?? 1) === 1 ? $t("warehouse_page.status_active") : $t("warehouse_page.status_inactive") }}
            </span>
          </template>
          <template v-else-if="column.key === 'actions'">
            <div class="d-flex justify-content-center gap-2">
              <button class="icon-btn" :title="$t('warehouse_page.edit')" @click="$emit('edit', record)">
                <i class="ri-pencil-line"></i>
              </button>
              <button class="icon-btn danger" :title="$t('warehouse_page.delete')" @click="$emit('delete', record)">
                <i class="ri-delete-bin-6-line"></i>
              </button>
            </div>
          </template>
          <template v-else-if="column.key === 'code'">
            <span class="code-pill">{{ text || $t("warehouse_page.not_available") }}</span>
          </template>
          <template v-else>
            {{ text || $t("warehouse_page.not_available") }}
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from "vue";
import { useI18n } from "vue-i18n";
import type { Warehouse } from "@/services/warehouse";

export default defineComponent({
  name: "WarehouseList",
  emits: ["update:searchQuery", "create", "edit", "delete"],
  props: {
    warehouses: {
      type: Array as PropType<Warehouse[]>,
      required: true,
    },
    searchQuery: {
      type: String,
      default: "",
    },
    paginationConfig: {
      type: Object as PropType<Record<string, unknown>>,
      required: true,
    },
  },
  setup() {
    const { t } = useI18n();
    const columns = computed(() => [
      { title: t("warehouse_page.table_code"), dataIndex: "code", key: "code", align: "center" as const, width: 150 },
      { title: t("warehouse_page.table_name"), dataIndex: "name", key: "name", align: "center" as const, width: 240 },
      { title: t("warehouse_page.table_phone"), dataIndex: "phone", key: "phone", align: "center" as const, width: 180 },
      { title: t("warehouse_page.table_email"), dataIndex: "email", key: "email", align: "center" as const, width: 220 },
      { title: t("warehouse_page.table_status"), dataIndex: "status", key: "status", align: "center" as const, width: 140 },
      { title: t("warehouse_page.table_action"), key: "actions", align: "center" as const, width: 140 },
    ]);

    return {
      columns,
    };
  },
});
</script>

<style scoped>
.toolbar-card {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 18px;
  padding: 22px;
  border: 1px solid #edf0f7;
  border-radius: 20px;
  background: #fbfcff;
}

.toolbar-right {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.search-shell {
  position: relative;
  min-width: 320px;
}

.toolbar-search {
  min-height: 54px;
  padding: 14px 48px 14px 18px;
  border-radius: 16px;
  border: 1px solid #d7def4;
  box-shadow: none;
}

.toolbar-search:focus {
  border-color: #605dff;
  box-shadow: 0 0 0 4px rgba(96, 93, 255, 0.12);
}

.search-icon {
  position: absolute;
  right: 18px;
  top: 50%;
  transform: translateY(-50%);
  color: #7d8aa2;
  pointer-events: none;
}

.warehouse-table :deep(.ant-table-thead > tr > th) {
  font-weight: 700;
}

.code-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 84px;
  padding: 6px 12px;
  border-radius: 999px;
  background: #eef2ff;
  color: #4f46e5;
  font-weight: 700;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 88px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.status-active {
  background: #e8f7ee;
  color: #1f9254;
}

.status-inactive {
  background: #fdecec;
  color: #d33c43;
}

.icon-btn {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid #e3e8f5;
  background: #fff;
  color: #4f46e5;
}

.icon-btn.danger {
  color: #d33c43;
}
</style>
